import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { getUnreadCount } from '@/services/notificationApi';

/**
 * Fetch and track the current user's unread notification count.
 *
 * - Polls every 30 s when unread count > 0; goes idle when count is 0 to
 *   avoid burning requests for users who have caught up on notifications.
 * - refetchOnWindowFocus is explicitly true — the global default in main.tsx
 *   is false, so this must be set explicitly to get tab-foreground refreshes.
 * - query key includes user.id to isolate caches when users switch accounts.
 * - 401 responses stop retries immediately (session expired / logged out).
 */
export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notification-unread-count', user?.id],
    queryFn: getUnreadCount,
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const data = q.state.data;
      return (data?.unread_count ?? 0) > 0 ? 30_000 : false;
    },
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
}
